// Product detail — a buyer's decision report for one product (Slice 5). Consumes
// the LIVE get_product_360 MCP tool via GET /api/product/{id}. This is the one
// screen that is ONLINE-only: it's a live call, not a cached read.
//
// Offline (or on a failed live call) we degrade — never block: show the cached
// Product Lookup basics from the snapshot (name, category, cost, retail, margin)
// with a "full report needs signal" note, so a tap still tells you something on
// the floor. Layout leads with the buyer's read (assessment summary + tags),
// then the decision block, seasonality, economics, buy-side, forecast, size mix.

import { fetchProduct360, isNotAuthed } from '../sync/api';
import { getIdentity } from '../auth/auth';
import { renderSessionExpired } from '../auth/sessionRecovery';
import { getSnapshot } from '../db/snapshot';
import type { CatalogRow } from '../snapshot/types';
import type { Product360, P360MonthUnits, P360Variant, P360Horizon } from './types';

export async function renderProduct360(
  root: HTMLElement,
  id: string,
  back: () => void,
): Promise<void> {
  const cached = await findCached(id);

  root.innerHTML = `
    <section class="card p360">
      <button type="button" class="link p360__back" id="p-back">‹ Back to Product Lookup</button>
      <div id="p-body">${loadingHtml(cached, id)}</div>
    </section>
  `;
  root.querySelector('#p-back')!.addEventListener('click', back);
  const body = root.querySelector<HTMLElement>('#p-body')!;

  // Offline → cached basics only; don't even attempt the live call.
  if (!navigator.onLine) {
    body.innerHTML = offlineHtml(cached, id);
    return;
  }

  // v1.2: reached by navigation rather than a tap on THIS screen, so a popup from
  // here would be blocked on iOS. Known-dead session → offer the button, and let
  // the retry carry the gesture.
  if (getIdentity().expired) {
    showExpired();
    return;
  }

  try {
    const report = await fetchProduct360(id, true);
    body.innerHTML = reportHtml(report, id);
    // Tap the left/right half of the chart → a panel listing that half's months.
    wireSeasonality(body, report.seasonality?.monthly_units ?? []);
  } catch (err) {
    if (isNotAuthed(err)) {
      showExpired();
      return;
    }
    // Live call failed — the backend self-reports (temp). Fall back to cached
    // basics if we have them, and show the reason either way.
    const detail = err instanceof Error ? err.message : String(err);
    body.innerHTML = errorHtml(cached, id, detail);
  }

  function showExpired(): void {
    renderSessionExpired(
      body,
      { what: "the live buyer's report", className: 'session-x--inline' },
      async () => {
        body.innerHTML = loadingHtml(cached, id);
        await renderProduct360(root, id, back);
      },
    );
  }
}

// ── Cached basics (offline / fallback) ──────────────────────────────────────
async function findCached(id: string): Promise<CatalogRow | null> {
  const snap = await getSnapshot();
  if (!snap) return null;
  return (
    snap.catalog.find((c) => c.sku === id) ??
    snap.catalog.find((c) => c.family === id) ??
    null
  );
}

function loadingHtml(cached: CatalogRow | null, id: string): string {
  const name = cached ? esc(cached.prod_name || cached.sku) : esc(id);
  return `
    <h2 class="p360__name">${name}</h2>
    <div class="p360__loading">Pulling the live buyer's report…</div>`;
}

function basicsHtml(cached: CatalogRow): string {
  const margin = marginPct(cached.latest_cost, cached.retail);
  return `
    <h2 class="p360__name">${esc(cached.prod_name || cached.sku)}</h2>
    <div class="p360__cat">${esc(cached.category || '—')}${cached.variant ? ` · ${esc(cached.variant)}` : ''}</div>
    <dl class="p360__grid">
      ${stat('Our cost', fmtMoney(cached.latest_cost))}
      ${stat('Retail', fmtRetail(cached.retail))}
      ${stat('Margin', margin != null ? `${margin}%` : '—')}
      ${stat('SKU', esc(cached.sku))}
    </dl>
    <div class="p360__family">${esc(cached.family)} · from cached snapshot</div>`;
}

function offlineHtml(cached: CatalogRow | null, id: string): string {
  const note = `<div class="p360__signal">📡 Offline — full report needs signal. Showing cached basics.</div>`;
  if (!cached) {
    return `${note}<div class="miss">No cached record for “${esc(id)}”. Reconnect for the full report.</div>`;
  }
  return `${note}${basicsHtml(cached)}`;
}

function errorHtml(cached: CatalogRow | null, id: string, detail: string): string {
  const note = `<div class="p360__signal p360__signal--warn">Full report unavailable — ${esc(detail)}</div>`;
  return cached ? `${note}${basicsHtml(cached)}` : `${note}<div class="miss">No cached basics for “${esc(id)}” either.</div>`;
}

// ── The live report ─────────────────────────────────────────────────────────
function reportHtml(r: Product360, id: string): string {
  // Non-fatal tool outcomes: unknown product, or an ambiguous name.
  if (r.error === 'product_not_found') {
    return `<div class="miss">No product found for “${esc(id)}”.</div>`;
  }
  if (r.status === 'multiple_matches') {
    const list = (r.candidates ?? [])
      .map((c) => `<li>${esc(c.name ?? c.sku ?? c.grain_key ?? '—')}${c.sku ? ` · ${esc(c.sku)}` : ''}</li>`)
      .join('');
    return `<div class="miss">“${esc(id)}” matches several products:<ul class="p360__cands">${list}</ul></div>`;
  }

  const idn = r.identity;
  const a = r.assessment;
  const cat = idn?.category_context;
  const catLine = cat ? [cat.department, cat.subDepartment].filter(Boolean).join(' · ') : '';

  return `
    ${idn ? `<h2 class="p360__name">${esc(idn.name)}</h2>` : ''}
    ${catLine ? `<div class="p360__cat">${esc(catLine)}</div>` : ''}

    ${assessmentHtml(a)}

    ${decisionHtml(r)}
    ${seasonalityHtml(r)}
    ${economicsHtml(r)}
    ${buySideHtml(r)}
    ${forecastHtml(r)}
    ${sizeMixHtml(r)}

    ${idn && idn.variant_count > 0 ? variantsHtml(idn.variants) : ''}
  `;
}

// Lead: the buyer's read — summary sentence + tag chips.
function assessmentHtml(a: Product360['assessment']): string {
  if (!a) return '';
  const tags = (a.tags ?? [])
    .map((t) => `<span class="p-tag">${esc(prettyTag(t))}</span>`)
    .join('');
  return `
    <div class="p360__assess">
      ${a.summary ? `<p class="p360__summary">${esc(a.summary)}</p>` : ''}
      ${tags ? `<div class="p-tags">${tags}</div>` : ''}
    </div>`;
}

// Decision block: velocity, months-of-supply + stock status, trend, YoY.
function decisionHtml(r: Product360): string {
  const d = r.demand;
  const inv = r.inventory;
  if (!d && !inv) return '';

  const vel = d?.velocity_per_month;
  const velVal = vel?.recent_3mo ?? vel?.lifetime;
  const velSub = vel?.lifetime != null ? `lifetime ${fmtNum(vel.lifetime)}/mo` : '';

  const mos = inv?.months_of_supply;
  const mosSub = inv?.stock_status ? stockBadge(inv.stock_status) : '';

  const yoy = d?.yoy;
  const yoyVal = yoy?.pct_change != null ? `${yoy.pct_change > 0 ? '▲' : yoy.pct_change < 0 ? '▼' : ''} ${Math.abs(yoy.pct_change)}%` : '—';
  const yoySub = yoy ? `${yoy.last_12mo_units} vs ${yoy.prior_12mo_units} units` : '';

  return `
    <h3 class="p360__h">Should we reorder?</h3>
    <dl class="p360__grid">
      ${stat('Velocity', velVal != null ? `${fmtNum(velVal)}/mo` : '—', velSub)}
      ${stat('Months of supply', mos != null ? fmtNum(mos) : '—', mosSub)}
      ${stat('Trend', trendChip(d?.trend), d ? `${d.units_18mo} units / 18mo` : '')}
      ${stat('Year over year', yoyVal, yoySub)}
    </dl>
    ${d ? `<div class="p360__reach">Bought by ${d.facilities_count} facilities · ${d.order_count} orders</div>` : ''}`;
}

// Small monthly seasonality sparkline (bar) + calendar peaks. The chart itself is
// split into a LEFT (earlier) and RIGHT (recent) tap-half; tapping a half opens a
// panel listing that half's months (see wireSeasonality). Bars stay visible for
// the trend read.
function seasonalityHtml(r: Product360): string {
  const s = r.seasonality;
  if (!s?.monthly_units?.length) return '';
  const months = s.monthly_units;
  const peaks = (s.peak_months ?? []).map((p) => p.month_name).join(', ');
  const splittable = months.length >= 2;
  return `
    <h3 class="p360__h">Seasonality <span class="p360__hsub">last ${s.window_months ?? months.length} mo</span></h3>
    <div class="p360__spark-wrap">
      ${sparkline(months)}
    </div>
    <div class="p360__spark-axis"><span>${esc(months[0].month)}</span><span>${esc(months[months.length - 1].month)}</span></div>
    ${splittable ? `<div class="p360__spark-hint">Tap the left or right half for monthly detail</div>` : ''}
    ${peaks ? `<div class="p360__peaks">Peaks: ${esc(peaks)}</div>` : ''}`;
}

/** Split months into an earlier + recent half (odd counts: the extra month goes
 *  to the earlier half). Used identically by the chart render and its wiring. */
function splitMonths(months: P360MonthUnits[]): { left: P360MonthUnits[]; right: P360MonthUnits[] } {
  const mid = Math.ceil(months.length / 2);
  return { left: months.slice(0, mid), right: months.slice(mid) };
}

// Wire the seasonality chart's two halves: tapping a half opens a panel listing
// that half's months (month · units · revenue). Replaces the old per-bar tooltip
// — a big two-target split is far easier to hit on an iPhone than a thin bar.
function wireSeasonality(root: HTMLElement, months: P360MonthUnits[]): void {
  const wrap = root.querySelector<HTMLElement>('.p360__spark-wrap');
  if (!wrap || months.length < 2) return;
  const { left, right } = splitMonths(months);

  wrap.querySelectorAll<SVGRectElement>('.p360__spark-half').forEach((half) => {
    const side = half.getAttribute('data-half');
    const isLeft = side === 'left';
    half.addEventListener('click', () => {
      const set = isLeft ? left : right;
      if (!set.length) return;
      openMonthPanel(isLeft ? 'Earlier months' : 'Recent months', set);
    });
  });
}

// A dialog-styled panel listing months (reuses the app's .confirm box + tokens).
// Dismisses on the ✕, a backdrop tap, or Esc.
function openMonthPanel(title: string, months: P360MonthUnits[]): void {
  const overlay = document.createElement('div');
  overlay.className = 'confirm';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  const rows = months
    .map(
      (m) => `
      <li class="p360__mrow">
        <span class="p360__mrow-mo">${esc(monthLabel(m.month))}</span>
        <span class="p360__mrow-u">${esc(String(m.units))} units</span>
        <span class="p360__mrow-r">${esc(fmtMoney(m.revenue))}</span>
      </li>`,
    )
    .join('');
  overlay.innerHTML = `
    <div class="confirm__box" role="document">
      <div class="p360__mhead">
        <h2 class="confirm__title">${esc(title)}</h2>
        <button type="button" class="p360__mclose" aria-label="Close">✕</button>
      </div>
      <ul class="p360__mlist">${rows}</ul>
    </div>`;
  document.body.appendChild(overlay);

  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  const close = (): void => {
    document.body.style.overflow = prevOverflow;
    window.removeEventListener('keydown', onKey);
    overlay.remove();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  window.addEventListener('keydown', onKey);
  overlay.querySelector('.p360__mclose')!.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
}

/** "2025-06" → "Jun 2025"; leaves anything unparseable untouched. */
function monthLabel(month: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(month);
  if (!m) return month;
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idx = Number(m[2]) - 1;
  return idx >= 0 && idx < 12 ? `${names[idx]} ${m[1]}` : month;
}

function economicsHtml(r: Product360): string {
  const e = r.economics;
  if (!e) return '';
  const marginPctVal = e.gross_margin_pct != null ? `${Math.round(e.gross_margin_pct * 100)}%` : '—';
  const below = e.below_cost_flag ? `<span class="p360__below">below cost</span>` : '';
  return `
    <h3 class="p360__h">Economics</h3>
    <dl class="p360__grid">
      ${stat('Our cost', fmtMoney(e.avg_cost), e.cost_direction && e.cost_direction !== 'flat' ? `cost ${esc(e.cost_direction)}` : '')}
      ${stat('Retail', fmtRetail(e.retail_price))}
      ${stat('Margin', `${marginPctVal} ${below}`, e.gross_margin_per_unit ? `${fmtMoney(e.gross_margin_per_unit)}/unit` : '')}
    </dl>`;
}

function buySideHtml(r: Product360): string {
  const b = r.buy_side;
  if (!b) return '';
  // Link each buy-side vendor to its Vendor 360 route (#/vendor/{id}).
  const vendors = (b.vendors ?? [])
    .map((v) => `<a class="p360__vendorlink" href="#/vendor/${encodeURIComponent(v.vendor_id)}">${esc(v.vendor_name || v.vendor_id)}</a>`)
    .join(', ') || '—';
  const single = b.single_source ? `<span class="p360__single">single source</span>` : '';
  const sell = b.sell_through_rate != null ? `${Math.round(b.sell_through_rate * 100)}%` : '—';
  return `
    <h3 class="p360__h">Where we buy it</h3>
    <div class="p360__vendors">${vendors} ${single}</div>
    <dl class="p360__grid">
      ${stat('Last cost', fmtMoney(b.last_unit_cost), b.last_po_date ? `PO ${fmtDate(b.last_po_date)}` : '')}
      ${stat('Units received', b.units_received != null ? String(b.units_received) : '—')}
      ${stat('Sell-through', sell)}
    </dl>`;
}

function forecastHtml(r: Product360): string {
  const f = r.forecast;
  if (!f) return '';
  if (f.forecast_suppressed) {
    return `<h3 class="p360__h">Forecast</h3><div class="p360__note">Forecast suppressed — ${esc(f.forecast_suppressed)}</div>`;
  }
  const conf = f.confidence_pct != null ? `${f.confidence_pct}% ${esc(f.confidence_tier ?? '')}` : '';
  return `
    <h3 class="p360__h">Forecast ${conf ? `<span class="p360__hsub">${conf} confidence</span>` : ''}</h3>
    <dl class="p360__grid p360__grid--3">
      ${horizon('3 mo', f.expected_3m)}
      ${horizon('6 mo', f.expected_6m)}
      ${horizon('12 mo', f.expected_12m)}
    </dl>`;
}

function sizeMixHtml(r: Product360): string {
  const m = r.size_mix;
  if (!m?.breakdown?.length) return '';
  const rows = m.breakdown
    .map((row) => {
      const pct = Math.round(num(row.share) * 100);
      return `
      <li class="p360__mix-row">
        <span class="p360__mix-label">${esc(row.size)}</span>
        <span class="p360__mix-bar"><span style="width:${pct}%"></span></span>
        <span class="p360__mix-pct">${pct}% · ${row.units}</span>
      </li>`;
    })
    .join('');
  return `
    <h3 class="p360__h">Ideal size / colour mix</h3>
    <ul class="p360__mix">${rows}</ul>
    ${m.notes ? `<div class="p360__note">${esc(m.notes)}</div>` : ''}`;
}

function variantsHtml(variants: P360Variant[]): string {
  const chips = variants
    .map((v) => `<span class="p360__var">${esc(v.variant_label)} · ${esc(v.prod_itemID)}</span>`)
    .join('');
  return `<div class="p360__variants"><span class="p360__varlbl">Variants</span>${chips}</div>`;
}

// ── bits ────────────────────────────────────────────────────────────────────
function stat(label: string, value: string, sub = ''): string {
  return `
    <div class="p360__stat">
      <dt>${label}</dt>
      <dd>${value}</dd>
      ${sub ? `<div class="p360__stat-sub">${sub}</div>` : ''}
    </div>`;
}

function horizon(label: string, h: P360Horizon | null): string {
  if (!h) return stat(label, '—');
  return `
    <div class="p360__stat">
      <dt>${label}</dt>
      <dd>${fmtNum(num(h.expected_units))}</dd>
      <div class="p360__stat-sub">${fmtNum(num(h.pi_low))}–${fmtNum(num(h.pi_high))}</div>
    </div>`;
}

function sparkline(months: P360MonthUnits[]): string {
  const max = Math.max(1, ...months.map((m) => m.units));
  const W = 100;
  const H = 34;
  const n = months.length;
  const bw = W / n;
  // Visible bars carry their own class so the fill rule targets ONLY them — the
  // full-height hit-halves below must stay transparent (a bare `rect` selector
  // would paint them solid over the chart).
  const bars = months
    .map((m, i) => {
      const h = (m.units / max) * H;
      const x = i * bw + bw * 0.12;
      const y = H - h;
      return `<rect class="p360__spark-bar" x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${(bw * 0.76).toFixed(2)}" height="${Math.max(h, 0.6).toFixed(2)}" rx="0.4"/>`;
    })
    .join('');

  // Two transparent full-height hit-halves (earlier | recent) + a faint divider,
  // so the whole chart is one big split tap target. Only rendered when splittable.
  let overlay = '';
  if (n >= 2) {
    const mid = Math.ceil(n / 2);
    const midX = mid * bw;
    overlay =
      `<line class="p360__spark-div" x1="${midX.toFixed(2)}" y1="0" x2="${midX.toFixed(2)}" y2="${H}"/>` +
      `<rect class="p360__spark-half" data-half="left" x="0" y="0" width="${midX.toFixed(2)}" height="${H}"/>` +
      `<rect class="p360__spark-half" data-half="right" x="${midX.toFixed(2)}" y="0" width="${(W - midX).toFixed(2)}" height="${H}"/>`;
  }
  return `<svg class="p360__spark" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img" aria-label="monthly units, last ${n} months">${bars}${overlay}</svg>`;
}

function stockBadge(status: string): string {
  const cls =
    status === 'reorder' ? 'p360__badge--warn' : status === 'overstocked' ? 'p360__badge--over' : 'p360__badge--ok';
  return `<span class="p360__badge ${cls}">${esc(status)}</span>`;
}

function trendChip(trend: string | undefined): string {
  if (!trend) return '—';
  const arrow = trend === 'growing' ? '▲' : trend === 'declining' ? '▼' : '→';
  const cls = trend === 'growing' ? 'p360__trend--up' : trend === 'declining' ? 'p360__trend--down' : 'p360__trend--flat';
  return `<span class="${cls}">${arrow} ${esc(trend)}</span>`;
}

function prettyTag(tag: string): string {
  // "seasonal_peak:summer/fall" → "seasonal peak: summer/fall"; "up_vs_last_year" → "up vs last year"
  return tag.replace(/_/g, ' ').replace(/:/g, ': ');
}

// ── formatting (shared idiom with the vendor screen) ────────────────────────
function num(v: string | number | null | undefined): number {
  if (v == null || v === '') return 0;
  const m = String(v).match(/-?\d+(\.\d+)?/); // tolerate range strings ("29.99-37.99")
  return m ? Number(m[0]) : 0;
}

function fmtNum(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function fmtMoney(v: string | number | null | undefined): string {
  const n = num(v);
  if (v == null || v === '' || !Number.isFinite(n)) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: n < 100 ? 2 : 0 })}`;
}

function fmtRetail(v: string | null | undefined): string {
  if (v == null || v === '') return '—';
  return /^\s*\d+(\.\d+)?\s*$/.test(v) ? fmtMoney(v) : esc(v);
}

function marginPct(cost: string | null, retail: string): number | null {
  const c = num(cost);
  const r = num(retail);
  return r > 0 && c > 0 ? Math.round(((r - c) / r) * 100) : null;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? esc(iso.slice(0, 10)) : d.toLocaleDateString();
}

function esc(s: string | null | undefined): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!,
  );
}
